/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: ClubDetail
 * File     : ClubDetail.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the ClubDetail view and coordinating data fetching.
 *   Exports: ClubDetail
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/clubdetail" element={<ClubDetail />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Stack, 
  Button, 
  Grid, 
  Chip, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Divider
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { 
  listClubPolls, 
  getClub, 
  listClubMembers, 
  listClubMembersPublic,
  addClubMember, 
  removeClubMember, 
  updateClubMember 
} from '../services/api';
import { useAuth } from '../context/AuthContext';

const ClubDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [polls, setPolls] = useState([]);
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [clubOwnerActive, setClubOwnerActive] = useState(true);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [newMemberUsername, setNewMemberUsername] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState('');
  const { loading, user } = useAuth();

  // Load club data and polls
  useEffect(() => {
    if (loading) return;
    let mounted = true;
    
    const loadData = async () => {
      try {
        // Load club details
        const clubData = await getClub(id);
        if (mounted) {
          setClub(clubData);
        }
        
        // Load polls
        const pollsData = await listClubPolls(id);
        if (mounted) {
          const rows = pollsData.polls || [];
          setPolls(rows);
        }
        
        // Load members for any club member
        if (mounted) {
          await loadMembers();
        }
        
        // If any poll carries club info and creator info, infer owner active from creator planStatus when creator matches owner
        try {
          // naive inference: if current user is owner and not active, show lock; else trust server when exposed later
          // Placeholder until backend adds club details endpoint with owner planStatus
          setClubOwnerActive(true);
        } catch (_) {}
      } catch (error) {
        console.error('Failed to load club data:', error);
        if (mounted) {
          setError('Failed to load club data');
        }
      }
    };
    
    loadData();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loading, user?.id]);

  // Check if user can manage members (Platform Owner/Admin or Club Creator)
  const canManageMembers = () => {
    if (!user || !club) return false;
    return user.role === 'owner' || user.role === 'admin' || 
           (club.owner && club.owner.id === user.id);
  };

  // Check if user can view members (any club member can view)
  // eslint-disable-next-line no-unused-vars
  const canViewMembers = () => {
    if (!user) return false;
    return true; // Always try to show members, let the API handle permissions
  };

  // Check if user is a member of the club
  const isClubMember = () => {
    if (!user || !members.length) return false;
    return members.some(member => member.userId === user.id);
  };

  // Load members
  const loadMembers = async () => {
    if (!user) return;
    
    setLoadingMembers(true);
    try {
      let membersData;
      
      // Use admin endpoint if user can manage, otherwise use public endpoint
      if (canManageMembers()) {
        membersData = await listClubMembers(id);
      } else {
        // Try to get public members list
        try {
          membersData = await listClubMembersPublic(id);
        } catch (error) {
          // If user is not a member, they can't see the list
          if (error.response?.status === 403) {
            setError('You must be a member of this club to view members');
            return;
          }
          throw error;
        }
      }
      
      setMembers(membersData.members || []);
    } catch (error) {
      console.error('Failed to load members:', error);
      setError('Failed to load members');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Add member
  const handleAddMember = async () => {
    if (!newMemberUsername.trim()) return;
    
    try {
      await addClubMember(id, newMemberUsername.trim(), newMemberRole);
      setNewMemberUsername('');
      setNewMemberRole('member');
      setAddMemberOpen(false);
      await loadMembers(); // Refresh members list
    } catch (error) {
      console.error('Failed to add member:', error);
      setError(error.response?.data?.error || 'Failed to add member');
    }
  };

  // Remove member
  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await removeClubMember(id, userId);
      await loadMembers(); // Refresh members list
    } catch (error) {
      console.error('Failed to remove member:', error);
      setError(error.response?.data?.error || 'Failed to remove member');
    }
  };

  // Update member role
  // eslint-disable-next-line no-unused-vars
  const handleUpdateMemberRole = async (userId, newRole) => {
    try {
      await updateClubMember(id, userId, newRole);
      await loadMembers(); // Refresh members list
    } catch (error) {
      console.error('Failed to update member role:', error);
      setError(error.response?.data?.error || 'Failed to update member role');
    }
  };

  const { activePolls, pastPolls } = useMemo(() => {
    const now = new Date();
    const active = [];
    const past = [];
    for (const p of polls) {
      const isActive = p.isActive && new Date(p.endDate) > now;
      (isActive ? active : past).push(p);
    }
    return { activePolls: active, pastPolls: past };
  }, [polls]);

  // Refetch when we focus the page (in case of background/tab switches)
  useEffect(() => {
    const onFocus = () => {
      if (loading) return;
      listClubPolls(id).then(r => setPolls(r.polls || [])).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [id, loading]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Club: {club?.name || `#${id}`}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {!clubOwnerActive && (
        <Chip color="warning" size="small" label="Host inactive: new polls are locked" sx={{ mb: 1 }} />
      )}
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        View polls created under this club. Members can follow discussions on each poll's results page.
      </Typography>
      
      {user && isClubMember() && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => navigate(`/clubs/${id}/streamer`)}
            size="large"
          >
            Open Club Streamer
          </Button>
        </Box>
      )}

      {/* Members Section */}
      {user && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Members</Typography>
            {canManageMembers() && (
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddMemberOpen(true)}
                size="small"
              >
                Add Member
              </Button>
            )}
          </Stack>
          
          <Paper sx={{ p: 2 }}>
            {loadingMembers ? (
              <Typography>Loading members...</Typography>
            ) : members.length === 0 ? (
              <Typography color="text.secondary">No members found.</Typography>
            ) : (
              <List>
                {members.filter(member => member && member.userId).map((member, index) => (
                  <React.Fragment key={member.userId}>
                    <ListItem>
                      <ListItemText
                        primary={
                          member.displayName 
                            ? `${member.displayName} (${member.username})`
                            : member.username
                        }
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            <Chip 
                              label={member.role === 'owner' ? 'Club Creator' : 'Member'} 
                              size="small" 
                              color={member.role === 'owner' ? 'primary' : 'default'}
                            />
                            {member.platformRole && member.platformRole !== 'user' && (
                              <Chip 
                                label={`Platform ${member.platformRole}`} 
                                size="small" 
                                variant="outlined"
                                color="info"
                              />
                            )}
                          </Stack>
                        }
                      />
                      {canManageMembers() && (
                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={1}>
                            {member.role !== 'owner' && (
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveMember(member.userId)}
                                color="error"
                                size="small"
                              >
                                <PersonRemoveIcon />
                              </IconButton>
                            )}
                          </Stack>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                    {index < members.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      )}

      {activePolls.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>Active Polls</Typography>
          <Grid container spacing={2}>
            {activePolls.filter(p => p && p.id).map(p => (
              <Grid item xs={12} md={6} key={p.id}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="subtitle1">{p.title}</Typography>
                      {p.endDate && (
                        <Typography variant="caption" color="text.secondary">Ends {new Date(p.endDate).toLocaleString()}</Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => navigate(`/vote?poll=${p.id}`)}>Vote</Button>
                      <Button size="small" onClick={() => navigate(`/results/${p.id}`)}>Results</Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>Past Polls</Typography>
        {pastPolls.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No past polls yet or you may not have access.</Typography>
        ) : (
          <Grid container spacing={2}>
            {pastPolls.filter(p => p && p.id).map(p => (
              <Grid item xs={12} md={6} key={p.id}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="subtitle1">{p.title}</Typography>
                      {p.winner?.movie && (
                        <Typography variant="caption" color="text.secondary">
                          Winner: {p.winner.movie.title}
                        </Typography>
                      )}
                    </Box>
                    <Button size="small" onClick={() => navigate(`/results/${p.id}`)}>View Discussion</Button>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member to Club</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            value={newMemberUsername}
            onChange={(e) => setNewMemberUsername(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="Enter username to add"
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={newMemberRole}
              onChange={(e) => setNewMemberRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="member">Member</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddMember} 
            variant="contained" 
            disabled={!newMemberUsername.trim()}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubDetail;


